import React from 'react';
import { useCustomizationContext } from '../../useCardCustomization';
import { ChoiceGroup } from '../../components/ChoiceGroup';
import { StyledSelect } from './CardDesign.styles';
import { useEditingMode } from '../../useEditingMode';
import { useQuantityDialog } from '../../useQuantityDialog';
import { useCardCustomizerTelemetry } from '../../CardCustomizer.telemetry';
import { usePrintCardsProductQuantityOptions } from '../../usePrintCardsProductQuantityOptions';
import { useTranslation } from '@shared/core';
import { CardEnvelopeRecipientAddressLayout } from '../../CardCustomizer.types';
import { IconV2, TextV2, TooltipV2 } from '@withjoy/joykit';
import { Warning } from '@withjoy/joykit/icons';

interface QuantityChoiceGroupProps {
  disabled?: boolean;
  /** Used to force a list of recipients for Recipients screens (fallbacks to card.envelope.recipientIdList.length if not provided) */
  recipientsCount?: number;
  /** Used to prevent the quantity dialog from being opened when the quantity is decreased (RecipientsV2 case) */
  ignoreQuantityDialog?: boolean;
}

export const QuantityChoiceGroup = ({ disabled, recipientsCount, ignoreQuantityDialog = false }: QuantityChoiceGroupProps) => {
  const { mode } = useEditingMode();
  const { customizationButtonInteracted } = useCardCustomizerTelemetry();
  const { tq } = useTranslation('stationery');

  const [cardEnvelope] = useCustomizationContext(draft => draft.config.card.envelope);
  const [_, update] = useCustomizationContext(draft => draft.config.customizations, mode === 'order');

  const { openQuantityDialog } = useQuantityDialog();
  const { options: quantityOptionsCalculated, selectedOption } = usePrintCardsProductQuantityOptions();

  return (
    <ChoiceGroup
      title={tq`Quantity`}
      value={`${selectedOption.value}`}
      getName={() => ''}
      price={selectedOption?.totalPriceString}
      icon={
        disabled ? (
          <TooltipV2
            content={
              <TextV2
                padding="5px"
                textAlign="center"
              >
                {tq`Changing Quantity is not available in edit mode. Please contact customer support.`}
              </TextV2>
            }
            placement="bottom"
            onMouseEnterDelay={500}
          >
            <IconV2
              size="md"
              cursor="pointer"
            >
              <Warning color="mono14" />
            </IconV2>
          </TooltipV2>
        ) : undefined
      }
    >
      <StyledSelect
        disabled={disabled}
        value={selectedOption.value}
        onChange={e => {
          const nextQty = Number(e.target.value);
          update(draft => {
            const count = recipientsCount ?? cardEnvelope.recipientIdList?.length ?? 0;
            const isLeaveBlankSelected = cardEnvelope.recipientAddressLayout === CardEnvelopeRecipientAddressLayout.none;

            if (nextQty < count && !ignoreQuantityDialog && !isLeaveBlankSelected) {
              openQuantityDialog(nextQty, count);
            } else {
              draft.quantity = nextQty;
            }
          });
          customizationButtonInteracted({ property: 'quantityOption', value: nextQty });
        }}
      >
        {quantityOptionsCalculated.map(opt => (
          <option
            value={opt.value}
            key={opt.key}
          >
            {opt.label}
          </option>
        ))}
      </StyledSelect>
    </ChoiceGroup>
  );
};
